import { DocumentTextIcon, PaperClipIcon } from "@heroicons/react/24/outline";
import React, { ReactNode } from "react";
import { useTranslation } from "react-i18next";

import { ApiResponse } from "@/api/rest/core/types";
import { WarrantyDraftFile } from "@/api/rest/services/warrantyService";
import UploadFilesPopup from "@/components/UploadFilesPopup.tsx";
import { Banner } from "@/components/common/Banner.tsx";
import Button from "@/components/common/Button";
import Spinner from "@/components/common/Spinner.tsx";
import UploadStatus from "@/components/common/UploadStatus";

interface SubmissionFormProps {
  children: ReactNode;
  handleSubmit: (e: React.FormEvent<HTMLFormElement>) => void;
  onPhoneChange: ((value: string) => void) | null;
  phoneNumber?: string;
  pendingFiles: File[];
  uploadedDraftFiles: WarrantyDraftFile[];
  isFileUploadMandatory: boolean;
  isFileUploaded: boolean | null;
  isSubmittingCase: boolean;
  submitFiles: (files: File[], onProgress: (fraction: number) => void) => Promise<ApiResponse<unknown>>;
  uploadProgress: number | null;
  submitError: string | null;
}

const SubmissionForm: React.FC<SubmissionFormProps> = ({
  children,
  handleSubmit,
  onPhoneChange,
  phoneNumber,
  pendingFiles,
  uploadedDraftFiles,
  isFileUploadMandatory,
  isFileUploaded,
  isSubmittingCase,
  submitFiles,
  uploadProgress,
  submitError,
}) => {
  const [showAnimation, setShowAnimation] = React.useState(false);
  const { t } = useTranslation();

  const handleFormSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (isFileUploadMandatory && !isFileUploaded) {
      setShowAnimation(true);
      setTimeout(() => setShowAnimation(false), 2000); // Reset after 2 seconds
      return;
    }
    handleSubmit(e);
  };

  return (
    <form className="flex self-stretch flex-col gap-4" onSubmit={handleFormSubmit}>
      {children}
      {(pendingFiles.length > 0 || uploadedDraftFiles.length > 0) && (
        <div className="flex flex-row flex-wrap gap-2">
          {uploadedDraftFiles.map((file) => (
            <span key={`draft-${file.entryId}`} className="rounded-md py-2 px-4 bg-gray-200 flex items-center">
              <DocumentTextIcon className="w-4 h-4 inline -mt-0.5 mr-2" />
              {file.filename ?? t("warranty.file")}
            </span>
          ))}
          {pendingFiles.map((file, i) => (
            <span key={i} className="rounded-md py-2 px-4 bg-gray-200 flex items-center">
              <DocumentTextIcon className="w-4 h-4 inline -mt-0.5 mr-2" />
              {file.name}
            </span>
          ))}
        </div>
      )}
      {isFileUploadMandatory && !isFileUploaded && (
        <Banner success={false} message={t("warranty.noFilesUploaded")} triggerAnimation={showAnimation} />
      )}
      <UploadFilesPopup className="flex flex-row" uploadCallback={submitFiles}>
        <Button
          type="button"
          variant="outline"
          className="rounded-md py-2 px-4 flex-grow !bg-orange-200 hover:!bg-orange-300 !text-orange-950"
          onClick={(event) => event.preventDefault()}
        >
          <PaperClipIcon className="w-4 h-4 inline -mt-1 ml-4 mr-1" />
          {t("warranty.addFiles")}
          <span className="w-4 h-4 inline ml-1 mr-4" />
        </Button>
      </UploadFilesPopup>
      {onPhoneChange && (
        <input
          className="p-2 rounded"
          type="tel"
          minLength={7}
          defaultValue={phoneNumber}
          onChange={(e) => onPhoneChange(e.target.value)}
          placeholder={t("warranty.contactPersonsPhoneNumber")}
          required
        />
      )}
      <UploadStatus progress={uploadProgress} error={submitError} />
      {!isSubmittingCase ? (
        <Button
          type="submit"
          title={isFileUploadMandatory ? t("warranty.noFilesUploaded") : ""}
          variant="primary"
          disabled={isFileUploadMandatory && !isFileUploaded}
          className={`${
            isFileUploadMandatory && !isFileUploaded
              ? "!bg-gray-400 cursor-not-allowed"
              : "!bg-blue-500 hover:!bg-blue-600"
          } text-white`}
        >
          {t("warranty.submitCase")}
        </Button>
      ) : (
        <span className="bg-orange-100 text-center rounded-md py-2 px-4 flex items-center justify-center">
          <Spinner className="w-5 h-5 inline mr-2" />
          {t("warranty.sendingCase")}
        </span>
      )}
    </form>
  );
};

export default SubmissionForm;
